import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

// Removed default close icon import

/**
 * AppDialog - A consistent dialog component for the application
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {function} props.onClose - Function to call when dialog should close
 * @param {string} props.title - Dialog title
 * @param {React.ReactNode} props.children - Dialog content
 * @param {React.ReactNode} props.actions - Custom action buttons (optional)
 * @param {string} props.primaryActionText - Text for primary action button (optional, default: "Save")
 * @param {function} props.onPrimaryAction - Function to call when primary action button is clicked
 * @param {boolean} props.loading - Whether the dialog is in a loading state
 * @param {string} props.maxWidth - Max width of dialog (xs, sm, md, lg, xl) - default: "md"
 */
const AppDialog = ({
  open,
  onClose,
  title,
  children,
  actions,
  primaryActionText = "Save",
  onPrimaryAction,
  loading = false,
  maxWidth = "md",
  DialogTitleProps = {},
  CloseButtonProps = {},
  ...props
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '1rem', // 16px border radius
          overflow: 'hidden',
          maxHeight: '90vh',
          height: '90%',
          width: "70vw",
          margin: 'auto',
          display: 'flex',
          flexDirection: 'column',
          ...(props.PaperProps?.sx || {})
        }
      }}
      {...props}
    >
      {/* Dialog Title with Close Button */}
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
          pb: '14px',
          pt: '14px',
          pl: 5,
          pr: '10px',
          bgcolor: 'white',
          ...(DialogTitleProps.sx || {})
        }}
      >
        <Typography variant="h6">{title}</Typography>
        <IconButton
          edge="end"
          onClick={onClose}
          aria-label="close"
          disabled={loading} size="small"
          sx={{
            color: '#adb5bd',
            mr: 0,
            ...(CloseButtonProps.sx || {})

          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Dialog Content */}
      <DialogContent sx={{ p: 0 }}> {/* Removed default padding to allow custom padding in children */}
        {children}
      </DialogContent>

      {/* Dialog Actions */}
      {(actions || onPrimaryAction) && (
        <DialogActions sx={{ px: 5, py: 3, justifyContent: 'flex-end' }}>
          {actions || (
            <Button
              variant="contained"
              onClick={onPrimaryAction}
              disabled={loading}
              sx={{
                bgcolor: 'var(--color-blue)',
                color: 'white',
                px: 4,
                py: 1.5,
                '&:hover': {
                  bgcolor: 'var(--color-blue-dark)',
                },
                borderRadius: '0px', // No border radius
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '1rem',
                minWidth: '120px',
                height: '48px',
                boxShadow: 'none', // No shadow
                border: 'none' // No border
              }}
            >
              {primaryActionText}
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default AppDialog;